import { AgentTurnType, ApprovalStepStatus } from 'ui-product-approvals-common/types/ApprovalData';
import { ApprovalRules } from 'ui-product-approvals-common/types/ApprovalRules';
import { serializeActor } from 'ui-product-approvals-common/utils/actorKey';
import { ACTOR } from '../constants/actors';
export const hasPendingAgentApprovalStep = steps => steps.some(step => step.approver.actor.actorType === ACTOR.AGENT && step.approvalStepStatus === ApprovalStepStatus.APPROVAL_PENDING);
export const groupRowsByOrder = approvalSteps => {
  const byOrder = new Map();
  const withoutOrder = [];
  approvalSteps.forEach(step => {
    var _byOrder$get;
    if (step.order === undefined) {
      withoutOrder.push(step.approver);
      return;
    }
    const group = (_byOrder$get = byOrder.get(step.order)) !== null && _byOrder$get !== void 0 ? _byOrder$get : [];
    group.push(step.approver);
    byOrder.set(step.order, group);
  });
  const orderedGroups = [...byOrder.entries()].sort(([orderA], [orderB]) => orderA - orderB).map(([, group]) => group);
  return withoutOrder.length > 0 ? [withoutOrder, ...orderedGroups] : orderedGroups;
};
export const groupStepsByOrder = approvalSteps => {
  const byOrder = new Map();
  approvalSteps.forEach(step => {
    var _step$order, _byOrder$get2;
    const order = (_step$order = step.order) !== null && _step$order !== void 0 ? _step$order : 0;
    const group = (_byOrder$get2 = byOrder.get(order)) !== null && _byOrder$get2 !== void 0 ? _byOrder$get2 : [];
    group.push(step);
    byOrder.set(order, group);
  });
  return [...byOrder.entries()].sort(([orderA], [orderB]) => orderA - orderB).map(([, group]) => group);
};
export const isGroupComplete = (group, approvalRules) => {
  const isApproved = step => step.approvalStepStatus === ApprovalStepStatus.APPROVED;
  return approvalRules === ApprovalRules.ANY_CAN_APPROVE ? group.some(isApproved) : group.every(isApproved);
};

// The status badge shown on a stage header in the redesigned Approvers section.
export let StageStatus;

// Resolves the stage-header badge: a stage that has a rejection reads REJECTED,
// a satisfied stage reads APPROVED, the active (first-incomplete) stage reads
// IN_PROGRESS while the approval is still open, and later stages read
// NOT_STARTED. A changes-requested step on the active stage stays IN_PROGRESS.
(function (StageStatus) {
  StageStatus["IN_PROGRESS"] = "IN_PROGRESS";
  StageStatus["NOT_STARTED"] = "NOT_STARTED";
  StageStatus["APPROVED"] = "APPROVED";
  StageStatus["REJECTED"] = "REJECTED";
})(StageStatus || (StageStatus = {}));
export const getStageStatus = (group, approvalRules, isActive) => {
  if (group.some(step => step.approvalStepStatus === ApprovalStepStatus.REJECTED)) {
    return StageStatus.REJECTED;
  }
  if (isGroupComplete(group, approvalRules)) {
    return StageStatus.APPROVED;
  }
  return isActive ? StageStatus.IN_PROGRESS : StageStatus.NOT_STARTED;
};

// Approver display order within a step/stage by actor type: individual users
// first, then teams, then agents.
const ACTOR_TYPE_DISPLAY_RANK = {
  [ACTOR.USER]: 0,
  [ACTOR.TEAM]: 1,
  [ACTOR.AGENT]: 2
};

// A step is "completed" once it has been acted on (approved, rejected, or
// changes requested), as opposed to still-open pending/waiting steps.
const COMPLETED_STEP_STATUSES = new Set([ApprovalStepStatus.APPROVED, ApprovalStepStatus.REJECTED, ApprovalStepStatus.CHANGES_REQUESTED]);

// Returns a new array ordered by: (1) steps the viewer can act on (non-empty
// availableOperations), (2) completed/acted-on steps, (3) remaining
// pending/waiting steps. Within each bucket, sort by actor type (user, team,
// agent) then alphabetically by display name.
// `approvalStatus`/`approvalRules` are retained for call-site stability while the
// ordering is being iterated on; the ranking no longer depends on them.
export const sortStepsDecisiveFirst = (steps, __approvalStatus, __approvalRules) => {
  const actionableRank = step => {
    var _step$availableOperat, _step$availableOperat2;
    return ((_step$availableOperat = (_step$availableOperat2 = step.availableOperations) === null || _step$availableOperat2 === void 0 ? void 0 : _step$availableOperat2.length) !== null && _step$availableOperat !== void 0 ? _step$availableOperat : 0) > 0 ? 0 : 1;
  };
  const completedRank = step => COMPLETED_STEP_STATUSES.has(step.approvalStepStatus) ? 0 : 1;
  return [...steps].sort((a, b) => actionableRank(a) - actionableRank(b) || completedRank(a) - completedRank(b) || ACTOR_TYPE_DISPLAY_RANK[a.approver.actor.actorType] - ACTOR_TYPE_DISPLAY_RANK[b.approver.actor.actorType] || a.approver.displayName.localeCompare(b.approver.displayName));
};
export const isOrderedApproval = steps => groupStepsByOrder(steps).length > 1;
export const getStepGroupCount = steps => groupStepsByOrder(steps).length;
export const getCompletedStepGroupCount = (steps, approvalRules) => groupStepsByOrder(steps).filter(group => isGroupComplete(group, approvalRules)).length;
export const getIncompleteStepGroupCount = (steps, approvalRules) => getStepGroupCount(steps) - getCompletedStepGroupCount(steps, approvalRules);
export const isStepApprovedOrRejected = step => step.approvalStepStatus === ApprovalStepStatus.APPROVED || step.approvalStepStatus === ApprovalStepStatus.REJECTED;
export const shouldWarnInvalidApprover = (step, validApproverActorKeys, isFetchApproversError, isLoadingApprovers) => !isFetchApproversError && !isLoadingApprovers && !isStepApprovedOrRejected(step) && !validApproverActorKeys.has(serializeActor(step.approver.actor));
export const isAgentReviewingApprovalStep = step => step.approvalStepStatus === ApprovalStepStatus.APPROVAL_PENDING && step.approver.actor.actorType === ACTOR.AGENT && step.inProgressTurnType === AgentTurnType.REVIEW;
export const toApprovalStepInputs = steps => steps.filter(step => step.approvers.length > 0).flatMap((step, index) => step.approvers.map(approver => ({
  actor: approver.actor,
  order: index
})));

/**
 * Returns whether a mutation changes the serialized stage order of an actor
 * protected by an individual/approved lock. A stage-level lock freezes a stage's
 * contents but not its position, so it does not protect order here. Empty draft
 * stages are omitted by {@link toApprovalStepInputs}, so structural index checks
 * alone cannot catch mutations that make a preceding stage empty or non-empty.
 */
export const haveLockedSerializedOrdersChanged = (before, after, lockedActorKeys) => {
  const protectedActorKeys = lockedActorKeys;
  const beforeOrders = new Map(toApprovalStepInputs(before).filter(input => protectedActorKeys.has(serializeActor(input.actor))).map(input => [serializeActor(input.actor), input.order]));
  const afterOrders = new Map(toApprovalStepInputs(after).map(input => [serializeActor(input.actor), input.order]));
  return [...beforeOrders].some(([actorKey, order]) => afterOrders.get(actorKey) !== order);
};
export const existingStepsToInputs = approvalSteps => approvalSteps.map(step => ({
  actor: step.approver.actor,
  order: step.order
}));
export const countDistinctApprovers = steps => {
  const actorKeys = new Set();
  steps.forEach(step => step.approvers.forEach(approver => actorKeys.add(serializeActor(approver.actor))));
  return actorKeys.size;
};
export const countOrderedSteps = steps => steps.filter(step => step.approvers.length > 0).length;
export const getApproverTrackingCounts = actors => {
  const seenKeys = new Set();
  const userKeys = new Set();
  const teamKeys = new Set();
  const agentIds = new Set();
  actors.forEach(actor => {
    const actorKey = serializeActor(actor);
    if (seenKeys.has(actorKey)) {
      return;
    }
    seenKeys.add(actorKey);
    if (actor.actorType === ACTOR.USER) {
      userKeys.add(actorKey);
    } else if (actor.actorType === ACTOR.TEAM) {
      teamKeys.add(actorKey);
    } else if (actor.actorType === ACTOR.AGENT) {
      agentIds.add(actor.id);
    }
  });
  return {
    userCount: userKeys.size,
    teamCount: teamKeys.size,
    agentIds: [...agentIds]
  };
};
export const hasEmptyStep = steps => steps.some(step => step.approvers.length === 0);
export const hasHumanApprover = steps => steps.some(step => step.approvers.some(approver => approver.actor.actorType === ACTOR.USER || approver.actor.actorType === ACTOR.TEAM));
export const stepHasLockedApprover = (step, lockedActorKeys) => step.approvers.some(approver => lockedActorKeys.has(serializeActor(approver.actor)));
export const isStepGroupComplete = (step, approvedActorKeys, approvalRules) => {
  if (step.approvers.length === 0) {
    return false;
  }
  const isApproved = approver => approvedActorKeys.has(serializeActor(approver.actor));
  return approvalRules === ApprovalRules.ANY_CAN_APPROVE ? step.approvers.some(isApproved) : step.approvers.every(isApproved);
};
export const hasValidStepReorderIndexes = (stepCount, fromIndex, toIndex) => Number.isInteger(fromIndex) && Number.isInteger(toIndex) && fromIndex >= 0 && fromIndex < stepCount && toIndex >= 0 && toIndex < stepCount;
const reorderChangesOrderMatching = (steps, fromIndex, toIndex, isOrderLocked) => {
  if (!hasValidStepReorderIndexes(steps.length, fromIndex, toIndex)) {
    return false;
  }
  const reordered = [...steps];
  const [moved] = reordered.splice(fromIndex, 1);
  if (moved === undefined) {
    return false;
  }
  reordered.splice(toIndex, 0, moved);
  return steps.some((step, index) => {
    var _reordered$index;
    return isOrderLocked(step) && ((_reordered$index = reordered[index]) === null || _reordered$index === void 0 ? void 0 : _reordered$index.id) !== step.id;
  });
};
export const stepOrderIsLocked = (step, lockedActorKeys) => stepHasLockedApprover(step, lockedActorKeys);

/**
 * Whether a reorder would move a stage whose *order* is locked. Only an
 * individual/approved approver lock protects order; a stage-level lock freezes a
 * stage's contents but leaves it freely reorderable.
 */
export const reorderChangesLockedOrder = (steps, fromIndex, toIndex, lockedActorKeys) => reorderChangesOrderMatching(steps, fromIndex, toIndex, step => stepOrderIsLocked(step, lockedActorKeys));
export const addingStepChangesLockedOrder = (steps, atIndex, lockedActorKeys) => steps.slice(Math.min(Math.max(atIndex, 0), steps.length)).some(step => stepOrderIsLocked(step, lockedActorKeys));
export const removingStepChangesLockedOrder = (steps, atIndex, lockedActorKeys) => atIndex >= 0 && steps.slice(atIndex + 1).some(step => stepOrderIsLocked(step, lockedActorKeys));

// Maps an insertion seam (gap index in [0, n]) to the splice index for
// onReorderSteps(from, to). Returns null when the seam is adjacent to the
// dragged step (a no-op move).
export const stepReorderToIndex = (fromIndex, seam) => {
  if (seam === fromIndex || seam === fromIndex + 1) {
    return null;
  }
  return fromIndex < seam ? seam - 1 : seam;
};

// Resolves the splice index for a step drag dropped at `seam`, or null when the
// drop is a no-op / not allowed (bad item, adjacent seam, or a locked-step move).
export const resolveStepDropIndex = (item, seam, canReorder) => {
  if (!item || typeof item.index !== 'number') {
    return null;
  }
  const toIndex = stepReorderToIndex(item.index, seam);
  return toIndex !== null && canReorder(item.index, toIndex) ? toIndex : null;
};
export const approvalStepsSignature = steps => steps.filter(step => step.approvers.length > 0).map(step => step.approvers.map(approver => serializeActor(approver.actor)).sort().join(',')).join('|');